/**
 * Block 2.1 (2026-05-26): Vacation API. Виробничий календар — api/production_calendar.ts.
 */
import { api } from './client';
import type { Page } from './catalogs';


// ============================== Vacation ==============================

export type VacationType =
  | 'ANNUAL_BASIC' | 'ANNUAL_ADDITIONAL' | 'SOCIAL' | 'UNPAID' | 'CREATIVE';


export interface VacationOrder {
  employee_name?: string | null;
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  employee_id: string;
  vacation_type: VacationType;
  vacation_start_date: string;
  vacation_end_date: string;
  days_count: number;
  reason: string | null;
  based_on_employee_request_date: string | null;
  is_paid: boolean;
  vacation_calculation_id: string | null;
  created_at: string;
  updated_at: string;
  external_source?: string | null;
}

export interface VacationOrderCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  employee_id: string;
  vacation_type: VacationType;
  vacation_start_date: string;
  vacation_end_date: string;
  days_count: number;
  reason?: string | null;
  based_on_employee_request_date?: string | null;
  is_paid?: boolean;
  comment?: string | null;
}

export interface VacationCalculation {
  employee_name?: string | null;
  comment?: string | null;
  id: string;
  organization_id: string;
  project_id?: string | null;
  document_number: string;
  document_date: string;
  posted: boolean;
  vacation_order_id: string;
  employee_id: string;
  vacation_type: VacationType;
  vacation_start_date: string;
  vacation_end_date: string;
  days_count: number;
  calc_period_start: string;
  calc_period_end: string;
  total_calendar_days_in_period: number;
  holiday_days_in_period: number;
  excluded_days_count: number;
  // TD-B-P5.1.4 (2026-06-01): ручний override з боку бухгалтера.
  excluded_days_override?: number | null;
  effective_days_in_period: number;
  total_earnings_12m: string;
  average_daily_wage: string;
  // ч.1118: з чого взято середню (EARNINGS / SALARY / MIN_SALARY / MANUAL) і
  // пояснення бухгалтеру (Порядок №100).
  calc_basis?: string;
  calc_note?: string | null;
  vacation_gross_amount: string;
  pdfo_rate: string;
  wz_rate: string;
  esv_rate: string;
  pdfo_amount: string;
  wz_amount: string;
  esv_amount: string;
  net_amount: string;
  cost_item_id: string | null;
  expense_account_code: string;
  external_source?: string | null;
  monthly_parts?: VacationMonthPart[];
}

export interface VacationMonthPart {
  period_year: number;
  period_month: number;
  days_in_month: number;
  gross_amount: string;
  pdfo_amount: string;
  wz_amount: string;
  esv_amount: string;
}

export interface CalculateVacationRequest {
  organization_id?: string;
  vacation_order_id: string;
  document_date?: string | null;
  pdfo_rate?: string;
  wz_rate?: string;
  esv_rate?: string;
  expense_account_code?: string;
  cost_item_id?: string | null;
  comment?: string | null;
  // TD-B-P5.1.4 (2026-06-01): ручний override (≥0 або null).
  excluded_days_override?: number | null;
  // Ручний override середньоденної (перебив у табличній частині).
  average_daily_override?: string | null;
}

export interface VacationCalculationPreview
  extends Omit<VacationCalculation,
    'id' | 'organization_id' | 'document_number' | 'document_date' | 'posted'
    | 'cost_item_id' | 'expense_account_code'> {
  // 0322: середня врахувала проведені мігровані (1С) нарахування → трикутник.
  average_from_import?: boolean;
}


const VO_BASE = '/documents/vacation-orders';
const VC_BASE = '/documents/vacation-calculations';


export const vacationOrder = {
  list: (params?: { organization_id?: string; employee_id?: string;
                    posted?: boolean; limit?: number; offset?: number;
                    sort_by?: string; sort_dir?: 'asc' | 'desc' }) =>
    api.get<Page<VacationOrder>>(VO_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<VacationOrder>(`${VO_BASE}/${id}`).then((r) => r.data),
  create: (p: VacationOrderCreate) =>
    api.post<VacationOrder>(VO_BASE, p).then((r) => r.data),
  update: (id: string, p: Partial<VacationOrderCreate>) =>
    api.patch<VacationOrder>(`${VO_BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${VO_BASE}/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<VacationOrder>(`${VO_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<VacationOrder>(`${VO_BASE}/${id}/unpost`).then((r) => r.data),
  printViewUrl: (id: string) => `${VO_BASE}/${id}/print-view`,
  // ч.1118: тривалість відпустки за календарем організації (зі щорічних відпусток
  // святкові дні прибираються — у воєнному режимі свят немає).
  daysCount: (params: { organization_id: string; vacation_type: VacationType;
                        start_date: string; end_date: string }) =>
    api.get<{ days_count: number; calendar_days: number; holiday_days: number }>(
      `${VO_BASE}/days-count`, { params }).then((r) => r.data),
  // V3: залишок днів відпустки працівника (м'який банер).
  employeeBalance: (employee_id: string, organization_id: string) =>
    api.get<{ employee_id: string; annual_basic_balance: string;
              annual_additional_balance: string }>(
      `${VO_BASE}/employee-balance`, { params: { employee_id, organization_id } },
    ).then((r) => r.data),
};

// V4: компенсація невикористаної відпустки при звільненні.
export interface VacationCompensationItem {
  comment?: string | null;
  id: string;
  organization_id: string;
  project_id?: string | null;
  document_number: string;
  document_date: string;
  posted: boolean;
  deletion_mark: boolean;
  dismissal_order_id: string;
  employee_id: string;
  compensation_days: string;
  dismiss_date: string;
  calc_period_start?: string;
  calc_period_end?: string;
  average_daily_wage: string;
  // ч.1118: пояснення розрахунку; дні, право на які набуто до 31.12.2023, і їхня
  // середньоденна з виплат 2023 року (Порядок №100, п.2).
  calc_basis?: string;
  calc_note?: string | null;
  days_before_2024?: string;
  average_daily_wage_2023?: string;
  compensation_gross_amount: string;
  pdfo_rate?: string;
  wz_rate?: string;
  esv_rate?: string;
  pdfo_amount: string;
  wz_amount: string;
  esv_amount: string;
  net_amount: string;
}

const VCOMP_BASE = '/documents/vacation-compensations';
export const vacationCompensation = {
  // Серверна пагінація/сортування + ПІБ (ag-Grid Infinite, 2026-08-23).
  page: (params: {
    organization_id: string; sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) => api.get<Page<VacationCompensationItem & { employee_name?: string | null }>>(
    `${VCOMP_BASE}/page`, { params }).then((r) => r.data),
  list: (organization_id: string, dismissal_order_id?: string) =>
    api.get<VacationCompensationItem[]>(VCOMP_BASE,
      { params: { organization_id, dismissal_order_id } }).then((r) => r.data),
  get: (id: string) =>
    api.get<VacationCompensationItem>(`${VCOMP_BASE}/${id}`).then((r) => r.data),
  update: (id: string, compensation_days: string, average_daily_wage: string,
           project_id?: string | null,
           expense_account_code?: string | null,
           comment?: string | null,
           pre2024?: { days_before_2024: string; average_daily_wage_2023: string }) =>
    api.patch<VacationCompensationItem>(`${VCOMP_BASE}/${id}`,
      { compensation_days, average_daily_wage,
        ...(project_id !== undefined ? { project_id } : {}),
        ...(expense_account_code ? { expense_account_code } : {}),
        ...(comment !== undefined ? { comment } : {}),
        ...(pre2024 ?? {}) }).then((r) => r.data),
  previewForDismissal: (organization_id: string, dismissal_order_id: string) =>
    api.post<Record<string, string>>(`${VCOMP_BASE}/preview-for-dismissal`,
      { organization_id, dismissal_order_id }).then((r) => r.data),
  previewByParams: (organization_id: string, employee_id: string, dismiss_date: string,
                    days_override?: string | null, average_daily_override?: string | null,
                    pre2024?: { days_before_2024?: string | null;
                                average_daily_wage_2023?: string | null }) =>
    api.post<Record<string, string>>(`${VCOMP_BASE}/preview-by-params`,
      { organization_id, employee_id, dismiss_date, days_override, average_daily_override,
        ...(pre2024 ?? {}) })
      .then((r) => r.data),
  fromDismissal: (organization_id: string, dismissal_order_id: string) =>
    api.post<VacationCompensationItem>(`${VCOMP_BASE}/from-dismissal`,
      { organization_id, dismissal_order_id }).then((r) => r.data),
  post: (id: string) =>
    api.post<VacationCompensationItem>(`${VCOMP_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<VacationCompensationItem>(`${VCOMP_BASE}/${id}/unpost`).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${VCOMP_BASE}/${id}`).then(() => undefined),
};


export const vacationCalculation = {
  list: (params?: { organization_id?: string; employee_id?: string;
                    posted?: boolean; limit?: number; offset?: number;
                    sort_by?: string; sort_dir?: 'asc' | 'desc' }) =>
    api.get<Page<VacationCalculation>>(VC_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<VacationCalculation>(`${VC_BASE}/${id}`).then((r) => r.data),
  preview: (p: CalculateVacationRequest) =>
    api.post<VacationCalculationPreview>(`${VC_BASE}/preview`, p)
      .then((r) => r.data),
  calculateAndPost: (p: CalculateVacationRequest) =>
    api.post<VacationCalculation>(`${VC_BASE}/calculate-and-post`, p)
      .then((r) => r.data),
  createDraft: (p: CalculateVacationRequest) =>
    api.post<VacationCalculation>(`${VC_BASE}/draft`, p).then((r) => r.data),
  update: (id: string, average_daily_override: string,
           excluded_days_override?: number | null,
           project_id?: string | null,
           expense_account_code?: string | null,
           comment?: string | null) =>
    api.patch<VacationCalculation>(`${VC_BASE}/${id}`,
      { average_daily_override, excluded_days_override,
        ...(project_id !== undefined ? { project_id } : {}),
        ...(expense_account_code ? { expense_account_code } : {}),
        ...(comment !== undefined ? { comment } : {}) }).then((r) => r.data),
  post: (id: string) =>
    api.post<VacationCalculation>(`${VC_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<VacationCalculation>(`${VC_BASE}/${id}/unpost`).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${VC_BASE}/${id}`).then(() => undefined),
};
